import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'en', label: 'English' },
  { code: 'pt', label: 'Português' },
  { code: 'tet', label: 'Tetum' },
];

// Simple SVG flag icons
const FlagIcons: Record<string, React.FC<{ className?: string }>> = {
  en: ({ className }) => (
    <svg className={className} viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg">
      <rect width="60" height="40" fill="#012169"/>
      <path d="M0,0 L60,40 M60,0 L0,40" stroke="#fff" strokeWidth="6"/>
      <path d="M0,0 L60,40 M60,0 L0,40" stroke="#C8102E" strokeWidth="2"/>
      <path d="M30,0 V40 M0,20 H60" stroke="#fff" strokeWidth="10"/>
      <path d="M30,0 V40 M0,20 H60" stroke="#C8102E" strokeWidth="6"/>
    </svg>
  ),
  pt: ({ className }) => (
    <svg className={className} viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg">
      {/* Green left portion */}
      <rect width="24" height="40" fill="#006847"/>
      {/* Red right portion */}
      <rect x="24" y="0" width="36" height="40" fill="#FF0000"/>
      {/* Yellow armillary sphere */}
      <circle cx="24" cy="20" r="10" fill="none" stroke="#FFD100" strokeWidth="2"/>
      <ellipse cx="24" cy="20" rx="10" ry="3" fill="none" stroke="#FFD100" strokeWidth="1.5"/>
      <ellipse cx="24" cy="20" rx="3" ry="10" fill="none" stroke="#FFD100" strokeWidth="1.5"/>
      <line x1="24" y1="9" x2="24" y2="31" stroke="#FFD100" strokeWidth="1"/>
      <line x1="13" y1="20" x2="35" y2="20" stroke="#FFD100" strokeWidth="1"/>
      {/* Red shield with yellow border */}
      <path d="M19,15 L29,15 L29,25 L24,30 L19,25 Z" fill="#DC143C" stroke="#FFD100" strokeWidth="1.5"/>
      {/* 5 blue shields in cross pattern */}
      <rect x="22" y="17" width="4" height="3" rx="0.5" fill="#003DA5"/>
      <rect x="22" y="22" width="4" height="3" rx="0.5" fill="#003DA5"/>
      <rect x="18" y="19.5" width="3" height="3" rx="0.5" fill="#003DA5"/>
      <rect x="27" y="19.5" width="3" height="3" rx="0.5" fill="#003DA5"/>
      <rect x="22.5" y="19.5" width="3" height="3" rx="0.5" fill="#003DA5"/>
      {/* White dots in shields */}
      {[22, 28, 18, 27, 22.5].map((x, i) => 
        [[0.5, 0.5], [1.5, 0.5], [2.5, 0.5], [0.5, 1.5], [1.5, 1.5], [2.5, 1.5]].map(([dx, dy]) => (
          <circle key={`${i}-${dx}-${dy}`} cx={x + dx} cy={17 + (i < 2 ? i * 5 : 2.5) + dy} r="0.4" fill="#fff"/>
        ))
      )}
    </svg>
  ),
  tet: ({ className }) => (
    <svg className={className} viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg">
      {/* Red background */}
      <rect width="60" height="40" fill="#DC1414"/>
      {/* Yellow triangle (larger, behind) */}
      <path d="M0,0 L28,20 L0,40 Z" fill="#FFCD00"/>
      {/* Black triangle */}
      <path d="M0,0 L24,20 L0,40 Z" fill="#000"/>
      {/* White 5-pointed star */}
      <polygon 
        points="9,14 10.5,18 15,18 11.5,20.5 13,25 9,22 5,25 6.5,20.5 3,18 7.5,18" 
        fill="#fff"
      />
    </svg>
  ),
};

interface LanguageSwitcherProps {
  variant?: 'dropdown' | 'buttons';
  className?: string;
  isScrolled?: boolean;
}

export function LanguageSwitcher({ variant: _variant = 'buttons', isScrolled: _isScrolled = true }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'en';

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  return (
    <div className="flex items-center gap-2">
      {languages.map((lang) => {
        const FlagIcon = FlagIcons[lang.code];
        const isActive = currentLang === lang.code;
        
        return (
          <button
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className="flex items-center justify-center w-9 h-6 rounded overflow-hidden transition-all hover:scale-110 border-2"
            style={{
              borderColor: isActive ? '#0284c7' : 'transparent',
              boxShadow: isActive ? '0 2px 8px rgba(2, 132, 199, 0.4)' : 'none',
            }}
            title={lang.label}
          >
            <FlagIcon className="w-full h-full" />
          </button>
        );
      })}
    </div>
  );
}